import React from 'react'
import { useParams } from 'react-router-dom'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { useStableHandler } from '@sketch/utils'
import { useAnalytics, RouteParams, routes } from '@sketch/modules-common'
import {
  useResponsiveDropdown,
  useBreakpoint,
  SFSymbolLabel,
  TruncateWithTooltip,
  InfiniteList,
  handleFetchMore,
} from '@sketch/components'
import { SidebarItem } from '../../../components/PageList/PageList.styles'
import {
  Container,
  Trigger,
  CurrentPage,
  Chevrons,
} from './PageSelector.styles'
import {
  useGetPreviewPagesQuery,
  GetPreviewPagesQuery,
} from '@sketch/gql-types'
import { usePreview } from '../../../views/PreviewView/PreviewContext'

const DROPDOWN_STYLES = {
  minWidth: 160,
  overflowY: 'auto',
  maxHeight: '80vh',
} as const

type Page = GetPreviewPagesQuery['preview']['pages']['entries'][0]

interface PageListProps {
  previewID: string
  pages: Page[]
  after: string | null
  fetchMore: any
}

const PageList = ({ pages, after, fetchMore, previewID }: PageListProps) => {
  const { trackEvent } = useAnalytics()

  const onPageClick = useStableHandler((pageUUID: string | null) => {
    /**
     * Do not track the `DOCUMENT - open page` event if:
     *  - user clicked on the "All Pages" link
     */
    const isAllPagesLink = pageUUID === null
    if (isAllPagesLink) {
      return
    }

    trackEvent('DOCUMENT - open page', {
      dropdown: true,
    })
  })

  return (
    <InfiniteList
      wrappingComponent={React.Fragment}
      onLoadMore={handleFetchMore(
        fetchMore,
        ['data', 'preview', 'pages', 'entries'],
        {
          dataIdFromObject,
          after,
        }
      )}
    >
      {pages.map(({ identifier, name, uuid }, index) => {
        let toRoute = ''

        if (index === 0) {
          toRoute = routes.PREVIEW_VIEW.create({ previewID })
        } else {
          toRoute = routes.PREVIEW_PAGE_VIEW.create({
            previewID,
            pageUUID: uuid || '',
          })
        }

        return (
          <SidebarItem
            key={identifier}
            data-testid="page-canvas-sidebar-item"
            to={toRoute}
            onClick={() => onPageClick(uuid)}
            exact
          >
            <TruncateWithTooltip>
              {name && <SFSymbolLabel text={name} />}
            </TruncateWithTooltip>
          </SidebarItem>
        )
      })}
    </InfiniteList>
  )
}

export default PageList

interface PageSelectorForPreviewProps {
  previewIdentifier: string
}

export const PageSelectorForPreview: React.FC<PageSelectorForPreviewProps> = ({
  previewIdentifier,
}) => {
  const isMobile = !useBreakpoint('sm')
  // selected page
  const { pageUUID } = useParams<RouteParams<'PREVIEW_PAGE_VIEW'>>()
  const previewContext = usePreview()

  const { data, loading, error, fetchMore } = useGetPreviewPagesQuery({
    variables: {
      previewIdentifier,
      password: previewContext?.password,
    },
  })

  const pages = data?.preview.pages.entries || []
  const pagesCount = data?.preview.pages.meta.totalCount

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: PageList,
    dropdownProps: {
      pages,
      after: data?.preview.pages.meta.after || null,
      fetchMore,
      previewID: previewIdentifier,
    },
    usePortal: true,
    placement: isMobile ? 'top-start' : 'bottom-start',
    dropdownStyle: DROPDOWN_STYLES,
    offset: [-8, 13], // 13 = 5 (usual offset) + 8 (canvas panel bottom padding)
  })

  if (loading) {
    // this loading is empty because we still have the general page loader rendering when this is happening
    return <></>
  }

  // this error is empty because we still have the general page error happening
  if (error || !data) {
    return <></>
  }

  const selectedPage = pages.find(page => page.uuid === pageUUID) || pages[0]
  const onlyOnePage = !!(pagesCount && pagesCount === 1) && !!selectedPage

  return (
    <Container>
      <Trigger
        {...buttonProps}
        aria-label="Canvas pages list"
        data-testid="pages-selector-trigger-preview"
        disabled={onlyOnePage}
      >
        <CurrentPage>
          {selectedPage
            ? selectedPage.name && <SFSymbolLabel text={selectedPage.name} />
            : 'Select page...'}
        </CurrentPage>
        {!onlyOnePage && <Chevrons />}
      </Trigger>
      {!onlyOnePage && content}
    </Container>
  )
}
